// src/components/sections/Galerie.jsx
import React, { useState } from 'react';
import { useTranslation } from '../../i18n';
import SectionTag from '../common/SectionTag';

// ✅ Liste des photos avec des IDs de catégorie (pas des noms en dur)
const PHOTOS = [
  {
    id: 1,
    src: '/photos/formation/formation1.jpeg',
    alt: 'Formation à Centre Culturelle Française (CCF) - 1',
    title: 'Formation CCF',
    category: 'formation'  // ← Utiliser des clés, pas des noms en dur
  },
  {
    id: 2,
    src: '/photos/formation/formation2.jpeg',
    alt: 'Formation à Centre Culturelle Française (CCF) - 2',
    title: 'Formation CCF',
    category: 'formation'
  },
  {
    id: 3,
    src: '/photos/formation/formation3.jpeg',
    alt: 'Formation à Centre Culturelle Française (CCF) - 3',
    title: 'Formation CCF',
    category: 'formation'
  },
  {
    id: 4,
    src: '/photos/formation/formation4.jpeg',
    alt: 'Formation à Centre Culturelle Française (CCF) - 4',
    title: 'Formation CCF',
    category: 'formation'
  },
  {
    id: 5,
    src: '/photos/formation/formation5.jpeg',
    alt: 'Formation à Centre Culturelle Française (CCF) - 5',
    title: 'Formation CCF',
    category: 'formation'
  },
  {
    id: 6,
    src: '/photos/orphelina/orphelina1.jpeg',
    alt: 'Aides aux orphelins',
    title: 'Aide aux orphelins',
    category: 'orphelins'  // ← Utiliser des clés
  },
  {
    id: 7,
    src: '/photos/Ramadan/ramadan1.jpeg',
    alt: 'Ramadan 2026',
    title: 'Ramadan 2026',
    category: 'ramadan'  // ← Utiliser des clés
  },
  {
    id: 8,
    src: '/photos/Ramadan/ramadan2.jpeg',
    alt: 'Ramadan 2026',
    title: 'Ramadan 2026',
    category: 'ramadan'
  }
];

const Galerie = () => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState('all');

  // ✅ Filtrer les photos par catégorie
  const getFilteredPhotos = () => {
    if (selectedCategory === 'all') {
      return PHOTOS;
    }
    return PHOTOS.filter(photo => photo.category === selectedCategory);
  };

  // ✅ Obtenir les catégories uniques
  const getCategories = () => {
    const categories = ['all'];
    PHOTOS.forEach(photo => {
      if (!categories.includes(photo.category)) {
        categories.push(photo.category);
      }
    });
    return categories;
  };

  const filteredPhotos = getFilteredPhotos();
  const categories = getCategories();

  // ✅ Fonction pour obtenir le libellé de catégorie traduit
  const getCategoryLabel = (cat) => {
    if (cat === 'all') return t('galerie_all');
    // Utiliser les clés de traduction définies
    const key = `galerie_category_${cat}`;
    return t(key) || cat;
  };

  // ✅ Fonction pour obtenir le libellé de catégorie pour le badge
  const getCategoryBadge = (cat) => {
    const key = `galerie_category_${cat}`;
    return t(key) || cat;
  };

  return (
    <section className="bg-white px-4 md:px-10 py-20" id="galerie">
      <div className="max-w-7xl mx-auto">
        <SectionTag>{t('galerie_tag')}</SectionTag>
        <h2 className="font-playfair text-3xl md:text-4xl font-bold">
          {t('galerie_title')}
        </h2>
        <p className="text-ofd-gray max-w-2xl mt-2">{t('galerie_desc')}</p>

        {/* ✅ Filtres par catégorie avec traductions */}
        {categories.length > 1 && (
          <div className="flex flex-wrap gap-2 mt-6 mb-6">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  selectedCategory === cat
                    ? 'bg-ofd-blue text-white'
                    : 'bg-ofd-gray-light text-ofd-slate hover:bg-ofd-blue/20'
                }`}
              >
                {getCategoryLabel(cat)}
                <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                  selectedCategory === cat
                    ? 'bg-white/20 text-white'
                    : 'bg-ofd-gray text-white'
                }`}>
                  {cat === 'all' ? PHOTOS.length : PHOTOS.filter(p => p.category === cat).length}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* ✅ Grille des photos */}
        {filteredPhotos.length === 0 ? (
          <div className="text-center py-20 text-ofd-gray mt-10">
            <div className="max-w-md mx-auto">
              <span className="text-7xl block mb-6">🖼️</span>
              <h3 className="text-2xl font-playfair font-bold text-ofd-slate mb-3">
                {t('galerie_no_photos')}
              </h3>
              <p className="text-sm text-ofd-gray/70">
                {t('galerie_photos_soon')}
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-6">
            {filteredPhotos.map((photo) => (
              <div
                key={photo.id}
                className="bg-ofd-gray-light rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group"
              >
                <div className="relative w-full bg-ofd-gray-light" style={{ paddingBottom: '75%' }}>
                  <img
                    src={photo.src}
                    alt={photo.alt}
                    className="absolute top-0 left-0 w-full h-full object-contain transition-transform duration-300 group-hover:scale-105 bg-ofd-gray-light"
                    loading="lazy"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const parent = e.target.parentElement;
                      parent.innerHTML = `
                        <div class="absolute inset-0 flex items-center justify-center text-ofd-gray">
                          <div class="text-center">
                            <span class="text-4xl block mb-2">🖼️</span>
                            <p class="text-sm">${t('galerie_image_not_available')}</p>
                          </div>
                        </div>
                      `;
                    }}
                  />
                </div>
                
                <div className="p-4">
                  <h4 className="font-semibold text-ofd-slate text-sm mb-1">
                    {photo.title || 'Photo OFD'}
                  </h4>
                  {photo.category && (
                    <span className="inline-block bg-ofd-blue/10 text-ofd-blue text-xs px-2 py-1 rounded-full">
                      {getCategoryBadge(photo.category)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 p-4 bg-ofd-gray-light/30 rounded-lg text-center text-ofd-gray text-sm">
          ℹ️ {t('galerie_info')}
        </div>
      </div>
    </section>
  );
};

export default Galerie;